/**
 * The roles and configuration the dialog needs to show what an archetype grants.
 *
 * Two sources, because the two tiers differ in kind. The clinic roles that decide
 * schedulability are fixed in code and mirrored on both sides, so only their ids
 * are needed. The discretionary agency roles are per-agency configuration, so
 * which ones an archetype grants has to be read from the server rather than
 * guessed here.
 *
 * Fetched when the dialog opens rather than per keystroke, and failure is
 * survivable: with no roles the Advanced section shows nothing and the request
 * omits the matrix, which leaves the server deriving it exactly as before.
 */

import { useEffect, useState } from 'react';
import { listRoles } from '../../../services/api';
import type { RoleResponse } from '../../../services/api';
import { getAgencyConfig } from '../../../services/agencyConfig';

export interface OnboardRoleContext {
  roles: RoleResponse[];
  effectiveArchetypeRoles: Record<string, string[]> | null;
  isLoading: boolean;
}

export function useOnboardRoleContext(open: boolean): OnboardRoleContext {
  const [roles, setRoles] = useState<RoleResponse[]>([]);
  const [effectiveArchetypeRoles, setEffective] = useState<Record<
    string,
    string[]
  > | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);

  useEffect(() => {
    if (!open || loadedOnce || isLoading) return;
    setIsLoading(true);
    Promise.all([
      listRoles({ query: { limit: 500 }, throwOnError: true }),
      // Tolerated separately: an operator without permission to read the
      // configuration can still add somebody, they just do not get the agency
      // roles pre-ticked.
      getAgencyConfig().catch(() => null),
    ])
      .then(([rolesRes, config]) => {
        setRoles(rolesRes.data ?? []);
        setEffective(
          (config?.effective_archetype_roles as Record<string, string[]>) ??
            null
        );
        setLoadedOnce(true);
      })
      .catch(err => {
        console.error('Failed to load roles for the add-member dialog:', err);
        setLoadedOnce(true);
      })
      .finally(() => setIsLoading(false));
  }, [open, loadedOnce, isLoading]);

  return { roles, effectiveArchetypeRoles, isLoading };
}
